import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ScrollToTop } from './components/ScrollToTop';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ExamProvider } from './contexts/ExamContext';
import { Toaster } from './components/ui/sonner';
import { SubscriptionRequired } from './components/SubscriptionRequired';
import { QuestionBankAccessRequired } from './components/QuestionBankAccessGate';
import { OnboardingGuard } from './components/OnboardingGuard';
import { useActivityTracker } from './hooks/useActivityTracker';
import { INSTITUTIONS_ENABLED } from './lib/featureFlags';
import { RACE_ENABLED } from './lib/featureFlags';
import { lazyNamed } from './lib/lazyNamed';
import { InstitutionProvider, useInstitution } from './contexts/InstitutionContext';
import { InstitutionMarketingRoute, InstitutionProtectedRoute, InstitutionStudentPortalRoute, InstitutionInviteRoute } from './components/institutions/InstitutionRouteGuard';
import {
  INSTITUTION_STUDENT_ONBOARDING_PATH,
  STUDENT_HOME_PATH,
  needsInstitutionStudentOnboarding,
  resolvePostLoginPath,
} from './lib/institutionOnboarding';
import { StudentPlatformGuard } from './components/institutions/StudentWorkspaceGuard';
import { Seo } from './components/seo/Seo';
import { FullPageLoader } from './components/ui/page-loader';
import { NotFoundPage } from './pages/NotFoundPage';

import { LandingPage } from './pages/LandingPage';
import { LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';

const PlatformPage = lazyNamed(() => import('./pages/marketing/PlatformPage'), 'PlatformPage');
const WhyMed900Page = lazyNamed(() => import('./pages/marketing/WhyMed900Page'), 'WhyMed900Page');
const PricingPage = lazyNamed(() => import('./pages/marketing/PricingPage'), 'PricingPage');
const AboutPage = lazyNamed(() => import('./pages/marketing/AboutPage'), 'AboutPage');
const ContactPage = lazyNamed(() => import('./pages/marketing/ContactPage'), 'ContactPage');
const FaqPage = lazyNamed(() => import('./pages/marketing/FaqPage'), 'FaqPage');
const VerbalReasoningPage = lazyNamed(() => import('./pages/marketing/UcatSectionPages'), 'VerbalReasoningPage');
const DecisionMakingPage = lazyNamed(() => import('./pages/marketing/UcatSectionPages'), 'DecisionMakingPage');
const QuantitativeReasoningPage = lazyNamed(() => import('./pages/marketing/UcatSectionPages'), 'QuantitativeReasoningPage');
const SituationalJudgementPage = lazyNamed(() => import('./pages/marketing/UcatSectionPages'), 'SituationalJudgementPage');
const ExamScreenshotPage = lazyNamed(() => import('./pages/ExamScreenshotPage'), 'ExamScreenshotPage');
const VisualQaPage = lazyNamed(() => import('./pages/VisualQaPage'), 'VisualQaPage');
const DashboardPage = lazyNamed(() => import('./pages/DashboardPage'), 'DashboardPage');
const PracticePage = lazyNamed(() => import('./pages/PracticePage'), 'PracticePage');
const PracticeHistoryPage = lazyNamed(() => import('./pages/PracticeHistoryPage'), 'PracticeHistoryPage');
const MocksPage = lazyNamed(() => import('./pages/MocksPage'), 'MocksPage');
const ExamPage = lazyNamed(() => import('./pages/ExamPage'), 'ExamPage');
const PracticeResultsPage = lazyNamed(() => import('./pages/PracticeResultsPage'), 'PracticeResultsPage');
const AnalyticsPage = lazyNamed(() => import('./pages/AnalyticsPage'));
const StudyPlanPage = lazyNamed(() => import('./pages/StudyPlanPage'));
const NotesPage = lazyNamed(() => import('./pages/NotesPage'), 'NotesPage');
const SettingsPage = lazyNamed(() => import('./pages/SettingsPage'), 'SettingsPage');
const BillingPage = lazyNamed(() => import('./pages/BillingPage'), 'BillingPage');
const BillingSuccessPage = lazyNamed(() => import('./pages/BillingSuccessPage'), 'BillingSuccessPage');
const GuidesPage = lazyNamed(() => import('./pages/GuidesPage'), 'GuidesPage');
const AdminPage = lazyNamed(() => import('./pages/AdminPage'), 'AdminPage');
const ImportReviewPage = lazyNamed(() => import('./pages/ImportReviewPage'), 'ImportReviewPage');
const AssetManagerPage = lazyNamed(() => import('./pages/AssetManagerPage'), 'AssetManagerPage');
const ManagerPage = lazyNamed(() => import('./pages/ManagerPage'), 'ManagerPage');
const JsonDocumentationPage = lazyNamed(() => import('./pages/JsonDocumentationPage'), 'JsonDocumentationPage');
const PlatformDocumentationPage = lazyNamed(() => import('./pages/PlatformDocumentationPage'), 'PlatformDocumentationPage');
const LogoPage = lazyNamed(() => import('./pages/LogoPage'));
const LogoPageLight = lazyNamed(() => import('./pages/LogoPage'), 'LogoPageLight');
const LogoPageAd = lazyNamed(() => import('./pages/LogoPage'), 'LogoPageAd');
const TermsPage = lazyNamed(() => import('./pages/TermsPage'), 'TermsPage');
const PrivacyPage = lazyNamed(() => import('./pages/PrivacyPage'), 'PrivacyPage');
const OnboardingPage = lazyNamed(() => import('./pages/OnboardingPage'), 'OnboardingPage');
const InstitutionsLandingPage = lazyNamed(() => import('./pages/institutions/InstitutionsLandingPage'), 'InstitutionsLandingPage');
const InstitutionLoginPage = lazyNamed(() => import('./pages/institutions/InstitutionLoginPage'), 'InstitutionLoginPage');
const InstitutionTrialPage = lazyNamed(() => import('./pages/institutions/InstitutionTrialPage'), 'InstitutionTrialPage');
const InstitutionDashboardPage = lazyNamed(() => import('./pages/institutions/InstitutionDashboardPage'), 'InstitutionDashboardPage');
const InstitutionStudentsPage = lazyNamed(() => import('./pages/institutions/InstitutionStudentsPage'), 'InstitutionStudentsPage');
const InstitutionCohortsPage = lazyNamed(() => import('./pages/institutions/InstitutionCohortsPage'), 'InstitutionCohortsPage');
const InstitutionAssignmentsPage = lazyNamed(() => import('./pages/institutions/InstitutionAssignmentsPage'), 'InstitutionAssignmentsPage');
const InstitutionAnalyticsPage = lazyNamed(() => import('./pages/institutions/InstitutionAnalyticsPage'), 'InstitutionAnalyticsPage');
const InstitutionSettingsPage = lazyNamed(() => import('./pages/institutions/InstitutionSettingsPage'), 'InstitutionSettingsPage');
const InstitutionMocksPage = lazyNamed(() => import('./pages/institutions/InstitutionMocksPage'), 'InstitutionMocksPage');
const InstitutionReviewPage = lazyNamed(() => import('./pages/institutions/InstitutionReviewPage'), 'InstitutionReviewPage');
const InstitutionQuestionSetsPage = lazyNamed(() => import('./pages/institutions/InstitutionQuestionSetsPage'), 'InstitutionQuestionSetsPage');
const InstitutionAssignmentFormPage = lazyNamed(() => import('./pages/institutions/InstitutionAssignmentFormPage'), 'InstitutionAssignmentFormPage');
const InstitutionMockFormPage = lazyNamed(() => import('./pages/institutions/InstitutionMockFormPage'), 'InstitutionMockFormPage');
const InstitutionStudentPortalPage = lazyNamed(() => import('./pages/institutions/InstitutionStudentPortalPage'), 'InstitutionStudentPortalPage');
const InstitutionAcceptInvitePage = lazyNamed(() => import('./pages/institutions/InstitutionAcceptInvitePage'), 'InstitutionAcceptInvitePage');
const InstitutionStudentOnboardingPage = lazyNamed(() => import('./pages/institutions/InstitutionStudentOnboardingPage'), 'InstitutionStudentOnboardingPage');
const StudentHomePage = lazyNamed(() => import('./pages/institutions/StudentHomePage'), 'StudentHomePage');
const RaceHubPage = lazyNamed(() => import('./pages/race/RaceHubPage'), 'RaceHubPage');
const RaceLivePage = lazyNamed(() => import('./pages/race/RaceLivePage'), 'RaceLivePage');
const RaceResultsPage = lazyNamed(() => import('./pages/race/RaceResultsPage'), 'RaceResultsPage');
const RaceHistoryPage = lazyNamed(() => import('./pages/race/RaceHistoryPage'), 'RaceHistoryPage');
const RaceReviewPage = lazyNamed(() => import('./pages/race/RaceReviewPage'), 'RaceReviewPage');
const RaceChallengePage = lazyNamed(() => import('./pages/race/RaceChallengePage'), 'RaceChallengePage');
const RaceProfilePage = lazyNamed(() => import('./pages/race/RaceProfilePage'), 'RaceProfilePage');

function usePrefetchAppChunks() {
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) return;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 400));
    const id = idle(() => {
      import('./pages/DashboardPage');
      import('./pages/PracticePage');
      import('./pages/MocksPage');
      import('./pages/AnalyticsPage');
    });
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [isAuthenticated]);
}

function CanonicalUrlNormalizer() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hostname === 'www.med900.com') {
      window.location.replace(`https://med900.com${pathname}${search}${hash}`);
    }
  }, [pathname, search, hash]);

  if (pathname.length > 1 && /\/+$/.test(pathname)) {
    return <Navigate to={`${pathname.replace(/\/+$/, '')}${search}${hash}`} replace />;
  }

  return null;
}

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <FullPageLoader message="Loading" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <Seo
        title="Med900 App"
        description="Med900 private UCAT preparation workspace."
        path={location.pathname}
        robots="noindex, nofollow"
      />
      <OnboardingGuard>
        {children}
      </OnboardingGuard>
    </>
  );
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, needsOnboarding, user, hasActiveSubscription } = useAuth();
  const { studentMemberships, allMemberships, loading: instLoading } = useInstitution();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  if (loading || (isAuthenticated && instLoading)) {
    return <FullPageLoader message="Loading" />;
  }

  if (isAuthenticated) {
    if (needsOnboarding) {
      const institutionOnboarding = needsInstitutionStudentOnboarding(user, studentMemberships);
      return (
        <Navigate
          to={institutionOnboarding ? INSTITUTION_STUDENT_ONBOARDING_PATH : '/onboarding'}
          replace
        />
      );
    }
    const dest = resolvePostLoginPath({
      redirectTo: searchParams.get('redirect') || location.state?.from,
      memberships: allMemberships,
      hasActiveSubscription,
    });
    return <Navigate to={dest} replace />;
  }

  return children;
};

const SubscribedRoute = ({ children, allowInstitutionPortal = false }) => (
  <PrivateAppShell>
    <OnboardingGuard>
      <StudentPlatformGuard>
        <SubscriptionRequired allowInstitutionPortal={allowInstitutionPortal}>
          {children}
        </SubscriptionRequired>
      </StudentPlatformGuard>
    </OnboardingGuard>
  </PrivateAppShell>
);

const PrivateAppShell = ({ children }) => {
  const location = useLocation();

  return (
    <>
      <Seo
        title="Med900 App"
        description="Med900 private UCAT preparation workspace."
        path={location.pathname}
        robots="noindex, nofollow"
      />
      {children}
    </>
  );
};

const QuestionBankRoute = ({ children }) => (
  <SubscribedRoute>
    <QuestionBankAccessRequired>
      {children}
    </QuestionBankAccessRequired>
  </SubscribedRoute>
);

const AppRoutes = () => {
  useActivityTracker();
  usePrefetchAppChunks();
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/why-med900" element={<WhyMed900Page />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/ucat/verbal-reasoning" element={<VerbalReasoningPage />} />
      <Route path="/ucat/decision-making" element={<DecisionMakingPage />} />
      <Route path="/ucat/quantitative-reasoning" element={<QuantitativeReasoningPage />} />
      <Route path="/ucat/situational-judgement" element={<SituationalJudgementPage />} />
      <Route path="/marketing/exam-preview" element={<ExamScreenshotPage />} />
      <Route path="/marketing/visual-qa" element={<VisualQaPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/signup" element={
        <PublicRoute>
          <SignupPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />

      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />

      {INSTITUTIONS_ENABLED && (
        <>
          <Route path={INSTITUTION_STUDENT_ONBOARDING_PATH} element={
            <ProtectedRoute>
              <InstitutionStudentOnboardingPage />
            </ProtectedRoute>
          } />
          <Route path={STUDENT_HOME_PATH} element={
            <ProtectedRoute>
              <StudentHomePage />
            </ProtectedRoute>
          } />
        </>
      )}

      <Route path="/dashboard" element={
        <SubscribedRoute>
          <DashboardPage />
        </SubscribedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      } />
      <Route path="/billing/success" element={
        <ProtectedRoute>
          <BillingSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {RACE_ENABLED && (
        <>
          <Route path="/race" element={
            <SubscribedRoute>
              <RaceHubPage />
            </SubscribedRoute>
          } />
          <Route path="/race/history" element={
            <SubscribedRoute>
              <RaceHistoryPage />
            </SubscribedRoute>
          } />
          <Route path="/race/results/:raceId/review" element={
            <SubscribedRoute>
              <RaceReviewPage />
            </SubscribedRoute>
          } />
          <Route path="/race/results/:raceId" element={
            <SubscribedRoute>
              <RaceResultsPage />
            </SubscribedRoute>
          } />
          <Route path="/race/challenge/:code" element={<RaceChallengePage />} />
          <Route path="/race/:raceId" element={
            <SubscribedRoute>
              <RaceLivePage />
            </SubscribedRoute>
          } />
          <Route path="/u/:handle" element={<RaceProfilePage />} />
        </>
      )}

      <Route path="/practice" element={
        <QuestionBankRoute>
          <PracticePage />
        </QuestionBankRoute>
      } />
      <Route path="/mocks" element={
        <SubscribedRoute>
          <MocksPage />
        </SubscribedRoute>
      } />
      <Route path="/exam" element={
        <SubscribedRoute allowInstitutionPortal>
          <ExamPage />
        </SubscribedRoute>
      } />
      <Route path="/exam/preview/:sessionId" element={
        <ProtectedRoute>
          <ExamPage />
        </ProtectedRoute>
      } />
      <Route path="/exam/:sessionId" element={
        <SubscribedRoute allowInstitutionPortal>
          <ExamPage />
        </SubscribedRoute>
      } />
      <Route path="/practice/results/:sessionId" element={
        <SubscribedRoute allowInstitutionPortal>
          <PracticeResultsPage />
        </SubscribedRoute>
      } />
      <Route path="/practice/history" element={
        <QuestionBankRoute>
          <PracticeHistoryPage />
        </QuestionBankRoute>
      } />
      <Route path="/trainers/*" element={<Navigate to="/dashboard" replace />} />
      <Route path="/analytics" element={
        <SubscribedRoute>
          <AnalyticsPage />
        </SubscribedRoute>
      } />
      <Route path="/study-plan" element={
        <SubscribedRoute>
          <StudyPlanPage />
        </SubscribedRoute>
      } />
      <Route path="/notes" element={
        <SubscribedRoute>
          <NotesPage />
        </SubscribedRoute>
      } />
      <Route path="/guides" element={
        <GuidesPage />
      } />
      <Route path="/guides/:slug" element={
        <GuidesPage />
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/:tab" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/:tab/:section" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/:tab/:section/:category" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/import" element={
        <ProtectedRoute>
          <ImportReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/assets" element={
        <ProtectedRoute>
          <AssetManagerPage />
        </ProtectedRoute>
      } />
      <Route path="/manager" element={
        <ProtectedRoute>
          <ManagerPage />
        </ProtectedRoute>
      } />
      <Route path="/json/documentation" element={<JsonDocumentationPage />} />
      <Route path="/platform/documentation" element={<PlatformDocumentationPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/logo" element={<LogoPage />} />
      <Route path="/logo/light" element={<LogoPageLight />} />
      <Route path="/logo/ad" element={<LogoPageAd />} />

      {/* Institutions B2B — INSTITUTIONS_PUBLIC in featureFlags.js controls public marketing */}
      {INSTITUTIONS_ENABLED ? (
        <>
          <Route path="/institutions" element={
            <InstitutionMarketingRoute><InstitutionsLandingPage /></InstitutionMarketingRoute>
          } />
          <Route path="/institutions/login" element={
            <InstitutionMarketingRoute><InstitutionLoginPage /></InstitutionMarketingRoute>
          } />
          <Route path="/institutions/trial" element={
            <InstitutionMarketingRoute><InstitutionTrialPage /></InstitutionMarketingRoute>
          } />
          <Route path="/institutions/dashboard" element={
            <InstitutionProtectedRoute><InstitutionDashboardPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/students" element={
            <InstitutionProtectedRoute><InstitutionStudentsPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/cohorts" element={
            <InstitutionProtectedRoute><InstitutionCohortsPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/assignments" element={
            <InstitutionProtectedRoute><InstitutionAssignmentsPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/assignments/new" element={
            <InstitutionProtectedRoute><InstitutionAssignmentFormPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/assignments/:assignmentId/edit" element={
            <InstitutionProtectedRoute><InstitutionAssignmentFormPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/question-sets" element={
            <InstitutionProtectedRoute><InstitutionQuestionSetsPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/analytics" element={
            <InstitutionProtectedRoute><InstitutionAnalyticsPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/settings" element={
            <InstitutionProtectedRoute><InstitutionSettingsPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/mocks" element={
            <InstitutionProtectedRoute><InstitutionMocksPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/mocks/new" element={
            <InstitutionProtectedRoute><InstitutionMockFormPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/mocks/:mockId/edit" element={
            <InstitutionProtectedRoute><InstitutionMockFormPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/review/:assignmentId" element={
            <InstitutionProtectedRoute><InstitutionReviewPage /></InstitutionProtectedRoute>
          } />
          <Route path="/institutions/accept-invite" element={
            <InstitutionInviteRoute><InstitutionAcceptInvitePage /></InstitutionInviteRoute>
          } />
          <Route path="/i/:slug" element={
            <InstitutionStudentPortalRoute><InstitutionStudentPortalPage /></InstitutionStudentPortalRoute>
          } />
        </>
      ) : (
        <>
          <Route path="/institutions/*" element={<Navigate to="/" replace />} />
          <Route path="/i/*" element={<Navigate to="/" replace />} />
        </>
      )}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <CanonicalUrlNormalizer />
      <ScrollToTop />
      <GoogleAnalytics />
      <ThemeProvider>
        <AuthProvider>
          <InstitutionProvider>
            <ExamProvider>
              <div className="App">
                <Suspense fallback={<FullPageLoader message="Loading" />}>
                  <AppRoutes />
                </Suspense>
                <Toaster />
              </div>
            </ExamProvider>
          </InstitutionProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
